/**
 * MobileExpandableTable — mobile-first accordion table for data lists.
 *
 * Below 768px, desktop data tables collapse to a card-stack pattern:
 *   - Header strip: title + count + optional CTA
 *   - Horizontally-scrollable filter chips with live counts
 *   - Row head: mark (cover/avatar/monogram) + title + meta + status + chevron
 *   - Tap row to reveal an inline detail panel (terracotta-tinted)
 *   - Footer with pagination
 *
 * Pulls all tokens from globals.css (`--surface-card`, `--terracotta-tint`,
 * `--divider`, etc.) so it auto-adapts to dark mode.
 *
 * @layer-1 — shared, used by every list page that has a desktop DataTable.
 */
import { useState, type ReactNode } from "react";
import { ChevronDown, Plus, Search } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useLocale } from "@/shared/hooks/useLocale";

interface MobileExpandableTableProps<T> {
  /** Row data — already filtered/paged by caller. */
  data: T[];
  /** Identity accessor — used as React key + open-state key. */
  getId: (row: T) => string | number;

  /** Sticky header: small section title (e.g. "All titles"). */
  title: string;
  /** Sub-line under the title: "5 of 34 titles". */
  countLabel: string;
  /** Optional CTA in the header (e.g. "New title"). */
  headerAction?: { label: string; icon?: ReactNode; onClick: () => void };

  /** Collapsed-row content slots. */
  renderMark: (row: T) => ReactNode;
  renderTitle: (row: T) => ReactNode;
  renderMeta?: (row: T) => ReactNode;
  renderBadge: (row: T) => ReactNode;

  /** Expanded-panel content. */
  renderDetail: (row: T) => ReactNode;

  /** Pagination. */
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  footerInfo?: string;

  /** Pre-open one row by id (default: first row in `data`). */
  initialOpenId?: string | number | null;

  /** Empty / loading. */
  isLoading?: boolean;
  emptyTitle?: string;
  emptyDesc?: string;
  onClearFilters?: () => void;
  clearFiltersLabel?: string;

  className?: string;
}

export function MobileExpandableTable<T>({
  data,
  getId,
  title,
  countLabel,
  headerAction,
  renderMark,
  renderTitle,
  renderMeta,
  renderBadge,
  renderDetail,
  page = 1,
  totalPages = 1,
  onPageChange,
  footerInfo,
  initialOpenId,
  isLoading,
  emptyTitle,
  emptyDesc,
  onClearFilters,
  clearFiltersLabel,
  className,
}: MobileExpandableTableProps<T>) {
  const { fmtNum } = useLocale();
  const [openId, setOpenId] = useState<string | number | null>(
    initialOpenId ?? (data[0] ? getId(data[0]) : null)
  );

  function toggle(id: string | number) {
    setOpenId((cur) => (cur === id ? null : id));
  }

  return (
    <div
      className={cn(
        "w-full overflow-hidden",
        "bg-[var(--card)] border border-[var(--border-light)]",
        "rounded-[20px]",
        className
      )}
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      {/* Header strip */}
      <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[var(--divider)]">
        <div>
          <div className="text-[14px] font-bold tracking-[-0.01em] text-[var(--text-primary)]">
            {title}
          </div>
          <div className="mt-0.5 text-[11px] tabular-nums text-[var(--text-tertiary)]">
            {countLabel}
          </div>
        </div>
        {headerAction && (
          <button
            type="button"
            onClick={headerAction.onClick}
            className={cn(
              "inline-flex items-center gap-1.5",
              "px-2.5 py-1.5 rounded-[9px]",
              "text-[11.5px] font-semibold",
              "bg-[var(--terracotta-tint)] text-[var(--gold-text)]",
              "border border-[var(--terracotta-border)]",
              "transition-colors hover:bg-[var(--surface-hover)]"
            )}
          >
            {headerAction.icon ?? <Plus size={12} strokeWidth={2.5} />}
            {headerAction.label}
          </button>
        )}
      </div>

      {/* Rows */}
      <div>
        {isLoading ? (
          <SkeletonRows />
        ) : data.length === 0 ? (
          <EmptyState
            title={emptyTitle}
            desc={emptyDesc}
            onClear={onClearFilters}
            clearLabel={clearFiltersLabel}
          />
        ) : (
          data.map((row) => {
            const id = getId(row);
            const open = openId === id;
            return (
              <div
                key={String(id)}
                className="border-b border-[var(--divider)] last:border-b-0"
              >
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  aria-expanded={open}
                  className={cn(
                    "flex items-center gap-3 w-full px-[18px] py-[14px]",
                    "text-start font-inherit transition-colors",
                    "hover:bg-[var(--surface-hover)]"
                  )}
                >
                  <div className="shrink-0">{renderMark(row)}</div>
                  <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    {renderTitle(row)}
                    {renderMeta && renderMeta(row)}
                  </div>
                  <div className="shrink-0">{renderBadge(row)}</div>
                  <ChevronDown
                    size={18}
                    strokeWidth={2}
                    className={cn(
                      "shrink-0 transition-transform duration-200",
                      open
                        ? "rotate-180 text-[var(--gold-text)]"
                        : "text-[var(--text-tertiary)]"
                    )}
                  />
                </button>
                <div
                  className={cn(
                    // grid-rows 0fr↔1fr animates the real content height, so the
                    // opening is an exact time-reverse of the closing (unlike the
                    // old max-height:900 hack, which snapped open and hung closed).
                    "grid transition-[grid-template-rows] duration-300 ease-out",
                    "bg-[var(--card)]",
                    open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  )}
                >
                  <div className="overflow-hidden">
                    <div
                      className={cn(
                        "mx-[18px] mb-4 mt-0 p-4",
                        "bg-[var(--terracotta-tint)] border border-[var(--terracotta-border)] rounded-[12px]"
                      )}
                    >
                      {renderDetail(row)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer pagination */}
      {!isLoading && data.length > 0 && onPageChange && (
        <div className="flex items-center justify-between px-[18px] py-3 border-t border-[var(--divider)] bg-[var(--surface-low)]">
          <span className="text-[11.5px] tabular-nums text-[var(--text-tertiary)]">
            {footerInfo}
          </span>
          <div className="flex gap-0.5">
            <PgBtn
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
              label="‹"
            />
            {pageNumbers(page, totalPages).map((p) => (
              <PgBtn
                key={p}
                active={p === page}
                onClick={() => onPageChange(p)}
                label={fmtNum(p)}
              />
            ))}
            <PgBtn
              disabled={page >= totalPages}
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              label="›"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

function pageNumbers(current: number, total: number): number[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, 4, 5];
  if (current >= total - 2)
    return [total - 4, total - 3, total - 2, total - 1, total];
  return [current - 2, current - 1, current, current + 1, current + 2];
}

function PgBtn({
  label,
  onClick,
  active,
  disabled,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "min-w-[28px] h-7 px-1.5 rounded-[7px]",
        "inline-flex items-center justify-center",
        "text-[12px] font-semibold tabular-nums",
        "transition-colors disabled:opacity-40 disabled:cursor-default",
        active
          ? "bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] border border-[var(--btn-primary-bg)]"
          : "text-[var(--text-tertiary)] border border-transparent hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
      )}
    >
      {label}
    </button>
  );
}

function SkeletonRows() {
  return (
    <div className="px-[18px] py-3 space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 py-2">
          <div className="w-11 h-11 rounded-[10px] bg-[var(--surface-low)] animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-3/5 rounded-full bg-[var(--surface-low)] animate-pulse" />
            <div className="h-2.5 w-2/5 rounded-full bg-[var(--surface-low)] animate-pulse" />
          </div>
          <div className="h-5 w-16 rounded-full bg-[var(--surface-low)] animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  title,
  desc,
  onClear,
  clearLabel,
}: {
  title?: string;
  desc?: string;
  onClear?: () => void;
  clearLabel?: string;
}) {
  return (
    <div className="px-6 py-14 text-center">
      <div className="w-14 h-14 rounded-full bg-[var(--surface-low)] mx-auto mb-3.5 flex items-center justify-center text-[var(--text-tertiary)]">
        <Search size={22} strokeWidth={1.6} />
      </div>
      {title && (
        <div className="text-[14px] font-semibold text-[var(--text-primary)] mb-1">
          {title}
        </div>
      )}
      {desc && (
        <div className="text-[12.5px] text-[var(--text-secondary)] leading-[1.5] max-w-[240px] mx-auto mb-3.5">
          {desc}
        </div>
      )}
      {onClear && clearLabel && (
        <button
          type="button"
          onClick={onClear}
          className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px]",
            "text-[12px] font-semibold",
            "bg-[var(--btn-secondary-bg)] text-[var(--btn-secondary-text)]",
            "border border-[var(--btn-secondary-border)]",
            "transition-colors hover:bg-[var(--surface-hover)]"
          )}
        >
          {clearLabel}
        </button>
      )}
    </div>
  );
}

/* ── Sub-components for caller convenience ───────────────────────────── */

/**
 * MobileKVGrid — 2-column key/value grid for use inside `renderDetail`.
 * Each item can opt into full-width via `fullWidth: true`.
 */
export function MobileKVGrid({
  items,
  className,
}: {
  items: { label: string; value: ReactNode; fullWidth?: boolean }[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-x-4 gap-y-3 mb-3.5",
        className
      )}
    >
      {items.map((it, i) => (
        <div
          key={i}
          className="min-w-0"
          style={it.fullWidth ? { gridColumn: "1 / -1" } : undefined}
        >
          <div className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-0.5">
            {it.label}
          </div>
          <div className="text-[12.5px] font-medium text-[var(--text-primary)] tabular-nums truncate">
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * MobileDetailHeader — primary contact / author cell at top of detail panel.
 */
export function MobileDetailHeader({
  initial,
  label,
  name,
}: {
  initial: string;
  label: string;
  name: string;
}) {
  return (
    <div className="flex items-center gap-2.5 mb-3.5 pb-3.5 border-b border-[var(--divider)]">
      <div
        className={cn(
          "w-[30px] h-[30px] rounded-full shrink-0",
          "inline-flex items-center justify-center",
          "text-[11.5px] font-bold",
          "bg-[var(--terracotta-tint)] text-[var(--gold-text)]",
          "border-[1.5px] border-[var(--terracotta-border)]"
        )}
      >
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-0.5">
          {label}
        </div>
        <div className="text-[13.5px] font-medium text-[var(--text-primary)] truncate">
          {name}
        </div>
      </div>
    </div>
  );
}

/**
 * MobileStatsStrip — 3-up stat row used by publisher detail panels.
 */
export function MobileStatsStrip({
  items,
}: {
  items: { value: ReactNode; label: string }[];
}) {
  return (
    <div className="grid grid-cols-3 mb-3.5 rounded-[10px] overflow-hidden bg-[var(--surface-low)] border border-[var(--divider)]">
      {items.map((it, i) => (
        <div
          key={i}
          className={cn(
            "px-2 py-2.5 text-center",
            i < items.length - 1 && "border-e border-[var(--divider)]"
          )}
        >
          <div className="text-[17px] font-extrabold tabular-nums leading-none tracking-[-0.01em] text-[var(--text-primary)]">
            {it.value}
          </div>
          <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-[var(--text-tertiary)] mt-1.5">
            {it.label}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * MobileActionRow — wraps row of action buttons inside the detail panel.
 */
export function MobileActionRow({ children }: { children: ReactNode }) {
  return <div className="flex gap-1.5 flex-wrap">{children}</div>;
}

/**
 * MobileDetailButton — labelled button used inside the detail panel.
 *
 * Variants:
 *   - primary  → ink fill
 *   - secondary → tinted neutral
 *   - approve  → green fill (uses published tokens)
 *   - danger   → transparent → red on hover
 */
export type MobileDetailButtonVariant =
  | "primary"
  | "secondary"
  | "approve"
  | "danger";

export function MobileDetailButton({
  variant = "primary",
  icon,
  children,
  onClick,
  disabled,
}: {
  variant?: MobileDetailButtonVariant;
  icon?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const variantClass = {
    primary:
      "bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] border-[var(--btn-primary-bg)] hover:bg-[var(--btn-primary-bg-hover)]",
    secondary:
      "bg-[var(--btn-secondary-bg)] text-[var(--btn-secondary-text)] border-[var(--btn-secondary-border)] hover:bg-[var(--surface-hover)]",
    approve:
      "bg-[var(--published-bg)] text-[var(--published-text)] border-[var(--published-bg)] hover:brightness-95",
    danger:
      "bg-transparent text-[var(--color-error-text)] border-[var(--color-error-bg)] hover:bg-[var(--color-error-bg)]",
  }[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] border",
        "text-[12px] font-semibold transition-colors",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variantClass
      )}
    >
      {icon}
      {children}
    </button>
  );
}
